import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { KeyRound, Loader2 } from "lucide-react";
import { api, errMsg, sharedRenderUrl } from "../lib/api";
import { LogoMark } from "../components/brand/Logo";

const Shell = ({ children, testid }) => (
  <div className="h-screen flex flex-col items-center justify-center gap-4 bg-[#F5F8FC] text-center px-6" data-testid={testid}>
    <LogoMark size={40} />{children}
  </div>
);

const PasscodeForm = ({ name, onUnlock }) => {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await onUnlock(code); } catch (x) { setErr(errMsg(x, "Wrong passcode")); } finally { setBusy(false); }
  };
  return (
    <Shell testid="share-passcode-page">
      <h1 className="text-2xl font-bold text-slate-900">{name}</h1>
      <p className="text-slate-500 text-sm flex items-center gap-1.5"><KeyRound size={14} className="text-amber-600" /> This preview is protected with a passcode.</p>
      <form onSubmit={submit} className="w-full max-w-xs space-y-3">
        <input value={code} onChange={(e) => setCode(e.target.value)} type="password" autoFocus placeholder="Passcode" data-testid="share-unlock-input"
          className="w-full h-11 px-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-900 text-center font-mono outline-none focus:border-sky-400" />
        {err && <p className="text-xs text-rose-600" data-testid="share-unlock-error">{err}</p>}
        <button disabled={busy || !code} data-testid="share-unlock-btn" className="w-full h-11 rounded-lg bg-slate-900 text-white font-semibold text-sm hover:bg-slate-700 disabled:opacity-50 flex items-center justify-center gap-2">
          {busy && <Loader2 size={15} className="animate-spin" />} Open preview
        </button>
      </form>
    </Shell>
  );
};

export default function SharedApp() {
  const { token } = useParams();
  const key = `ishukart_share_${token}`;
  const [meta, setMeta] = useState(undefined);
  const [access, setAccess] = useState(() => sessionStorage.getItem(key));

  useEffect(() => {
    api.shareMeta(token).then((m) => { setMeta(m); document.title = `${m.name} · Preview`; if (m.access) setAccess(m.access); }).catch(() => setMeta(null));
  }, [token]);

  const unlock = async (code) => {
    const r = await api.shareUnlock(token, code);
    sessionStorage.setItem(key, r.access);
    setAccess(r.access);
  };

  if (meta === undefined) return <div className="h-screen flex items-center justify-center bg-[#F5F8FC]"><Loader2 className="animate-spin text-emerald-600" /></div>;
  if (meta === null) return <Shell testid="share-not-found"><h1 className="text-2xl font-bold text-slate-900">This link isn't active</h1><p className="text-slate-500 text-sm">The owner may have turned it off or created a new one.</p><Link to="/" className="text-emerald-600 text-sm hover:underline">Build your own with Ishukart</Link></Shell>;
  if (!meta.has_version) return <Shell testid="share-empty"><h1 className="text-2xl font-bold text-slate-900">{meta.name}</h1><p className="text-slate-500 text-sm">Nothing to preview yet.</p></Shell>;
  if (!access) return <PasscodeForm name={meta.name} onUnlock={unlock} />;

  return (
    <div className="fixed inset-0 bg-white" data-testid="shared-app">
      <iframe title={meta.name} src={sharedRenderUrl(token, access)} className="w-full h-full border-0" data-testid="shared-iframe"
        onError={() => { sessionStorage.removeItem(key); setAccess(null); }}
        sandbox="allow-scripts allow-forms allow-popups allow-modals allow-downloads allow-popups-to-escape-sandbox" />
      <span className="fixed bottom-3 right-3 flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-white/90 backdrop-blur border border-slate-300 text-[11px] text-slate-600 shadow-lg" data-testid="shared-badge">
        <LogoMark size={16} /> Private preview
      </span>
    </div>
  );
}
